import {
  MathFunctions,
  Vec2,
  Vec3,
  BooleanParameter,
  NumberParameter,
  Vec2Parameter,
  Vec3Parameter,
  ListParameter,
  StructParameter,
  TreeItemParameter,
  Operator,
  OperatorOutputMode,
  Registry,
  Xfo,
  XfoOperatorOutput,
} from '@zeainc/zea-engine'

/** Class representing an explode part parameter.
 * @extends StructParameter
 * @private
 */
class ExplodePartParameter extends StructParameter {
  stageParam = new NumberParameter('Stage', 0)
  axisParam = new Vec3Parameter('Axis', new Vec3(1, 0, 0))
  movementParam = new Vec2Parameter('MovementTiming', new Vec2(0, 1), [new Vec2(0, 0), new Vec2(1, 1)])
  multiplierParam = new NumberParameter('Multiplier', 1.0)
  output = new XfoOperatorOutput('output', OperatorOutputMode.OP_READ_WRITE)
  /**
   * Create an explode part parameter.
   * @param {string} name - The name value.
   */
  constructor(name = '') {
    super(name)

    this.addMember(this.stageParam)
    this.addMember(this.axisParam)
    // The Movement param enables fine level timing to be set per part.
    this.addMember(this.movementParam)
    this.addMember(this.multiplierParam)
  }

  /**
   * The getStage method.
   * @return {any} - The return value.
   */
  getStage(): number {
    return this.stageParam.getValue()
  }

  /**
   * The setStage method.
   * @param {any} stage - The stage value.
   */
  setStage(stage: number) {
    this.stageParam.setValue(stage)
  }

  /**
   * The getOutput method.
   * @return {XfoOperatorOutput} - The return value.
   */
  getOutput(): XfoOperatorOutput {
    return this.output
  }

  /**
   * The evaluate method.
   * @param {any} explode - The explode value.
   * @param {any} explodeDist - The distance that the parts explode to.
   * @param {any} offset - The offset value.
   * @param {any} stages - The stages value.
   * @param {any} cascade - In "cascade" mode, the parts move in a cascade.
   * @param {any} centered - The centered value.
   * @param {Xfo} parentXfo - The parentXfo value.
   * @param {any} parentDelta - The parentDelta value.
   */
  evaluate(
    explode: number,
    explodeDist: number,
    offset: number,
    stages: number,
    cascade: boolean,
    centered: boolean,
    parentXfo: Xfo,
    parentDelta: Xfo
  ) {
    // Note: during interactive setup of the operator we
    // can have evaluations before anhthing is connected.
    if (!this.output.isConnected()) return

    const stage = this.stageParam.getValue()
    const movement = this.movementParam.getValue()
    let dist
    if (cascade) {
      // In 'cascade' mode, the parts move in a cascade,
      // starting with stage 0. then 1 ...
      let t = stage / stages
      if (centered) t -= 0.5
      dist = explodeDist * MathFunctions.linStep(movement.x, movement.y, Math.max(0, explode - t))
    } else {
      // Else all the parts are spread out across the explode distance.
      let t = 1.0 - stage / stages
      if (centered) t -= 0.5
      dist = explodeDist * MathFunctions.linStep(movement.x, movement.y, explode) * t
    }
    dist += offset

    let explodeDir = this.axisParam.getValue()
    const multiplier = this.multiplierParam.getValue()
    let xfo = this.output.getValue()
    if (parentXfo) {
      xfo = parentDelta.multiply(xfo)
      explodeDir = parentXfo.ori.rotateVec3(explodeDir)
    }
    xfo.tr.addInPlace(explodeDir.scale(dist * multiplier))
    this.output.setClean(xfo)
  }

  // ////////////////////////////////////////
  // Persistence

  /**
   * The toJSON method encodes this type as a json object for persistence.
   * @param {object} context - The context value.
   * @return {object} - Returns the json object.
   */
  toJSON(context: Record<string, any>): Record<string, any> {
    const j = super.toJSON(context)
    if (j) {
      j.output = this.output.toJSON(context)
    }
    return j
  }

  /**
   * The fromJSON method decodes a json object for this type.
   * @param {object} j - The json object this item must decode.
   * @param {object} context - The context value.
   */
  fromJSON(j: Record<string, any>, context: Record<string, any>) {
    super.fromJSON(j, context)
    if (j.output) {
      this.output.fromJSON(j.output, context)
    }
  }
}

/** Class representing an explode parts operator.
 * @extends ParameterOwner
 */
class ExplodePartsOperator extends Operator {
  stagesParam = new NumberParameter('Stages', 0)
  explodeParam = new NumberParameter('Explode', 0.0, [0, 1])
  distParam = new NumberParameter('Dist', 1.0)
  offsetParam = new NumberParameter('Offset', 0)
  cascadeParam = new BooleanParameter('Cascade', false)
  centeredParam = new BooleanParameter('Centered', false)
  parentItemParam = new TreeItemParameter('RelativeTo')
  invParentSpace: Xfo
  itemsParam = new ListParameter('Parts', typeof ExplodePartParameter)
  localXfos: Array<Xfo>
  /**
   * Create an explode parts operator.
   * @param {string} name - The name value.
   */
  constructor(name?: string) {
    super(name)

    this.addParameter(this.stagesParam)
    this.addParameter(this.explodeParam)
    this.addParameter(this.distParam)
    this.addParameter(this.offsetParam)
    this.addParameter(this.cascadeParam)
    this.addParameter(this.centeredParam)
    this.addParameter(this.parentItemParam)

    this.parentItemParam.on('valueChanged', () => {
      // compute the local xfos
      const parentItem = this.parentItemParam.getValue()
      if (parentItem) this.invParentSpace = parentItem.getParameter('GlobalXfo').getValue().inverse()
      else this.invParentSpace = undefined
    })
    this.parentItemParam.on('treeItemGlobalXfoChanged', () => {
      this.setDirty()
    })

    this.addParameter(this.itemsParam)
    this.itemsParam.on('elementAdded', (event) => {
      if (event.index > 0) {
        const explodeParam = <ExplodePartParameter>this.itemsParam.getElement(event.index - 1)
        const prevStage = explodeParam.getStage()
        event.elem.setStage(prevStage + 1)
        this.stagesParam.setValue(prevStage + 2)
      } else {
        this.stagesParam.setValue(1)
      }
      event.elem.output = new XfoOperatorOutput('Part' + event.index, OperatorOutputMode.OP_READ_WRITE)
      this.addOutput(event.elem.getOutput())
      this.setDirty()
    })
    this.itemsParam.on('elementRemoved', (event) => {
      this.removeOutput(event.elem.getOutput())
    })
    this.addParameter(this.itemsParam)

    this.localXfos = []
  }

  addPart(): ExplodePartParameter {
    const part = new ExplodePartParameter()
    this.itemsParam.addElement(part)
    return part
  }

  /**
   * The evaluate method.
   */
  evaluate(): void {
    // console.log(`Operator: evaluate: ${this.getName()}`)
    const stages = this.stagesParam.getValue()
    const explode = this.explodeParam.getValue()
    // const explodeDir = this.axisParam.getValue();
    const explodeDist = this.distParam.getValue()
    const offset = this.offsetParam.getValue()
    const cascade = this.cascadeParam.getValue()
    const centered = this.centeredParam.getValue()
    const parentItem = this.parentItemParam.getValue()
    let parentXfo
    let parentDelta
    if (parentItem) {
      parentXfo = parentItem.getParameter('GlobalXfo').getValue()
      parentDelta = this.invParentSpace.multiply(parentXfo)
    }

    const items = this.itemsParam.getValue()
    for (let i = 0; i < items.length; i++) {
      const part = items[i]
      part.evaluate(explode, explodeDist, offset, stages, cascade, centered, parentXfo, parentDelta)
    }
  }

  // ////////////////////////////////////////
  // Persistence

  /**
   * The toJSON method encodes this type as a json object for persistence.
   *
   * @param {object} context - The context value.
   * @return {object} - Returns the json object.
   */
  toJSON(context: Record<string, any>): Record<string, any> {
    return super.toJSON(context)
  }

  /**
   * The fromJSON method decodes a json object for this type.
   * @param {object} j - The json object this item must decode.
   * @param {object} context - The context value.
   */
  fromJSON(j: Record<string, any>, context: Record<string, any>) {
    super.fromJSON(j, context)
  }
}

Registry.register('ExplodePartsOperator', ExplodePartsOperator)

export { ExplodePartsOperator }
